/**
 * Central country/locale config — frontend mirror of backend COUNTRY_CONFIG.
 *
 * Source of truth: `window.location.hostname` (production), with
 * `REACT_APP_DEFAULT_COUNTRY_CODE` env override for preview/staging.
 *
 * Used by:
 *   - CountryContext (auto-detects on mount)
 *   - SignupPage, ProviderPages, HomePage, AboutPage, Footer (display + form labels)
 *   - QuoteRequestPage, QuoteDetailPage (currency display)
 *
 * NOTE: keep in sync with backend `/app/backend/server.py: COUNTRY_CONFIG`
 * so the two sides agree on labels + validation rules.
 */

// ── Locale config (NZ / AU) ─────────────────────────────────────────
export const COUNTRY_CONFIG = {
  NZ: {
    code: 'NZ',
    name: 'New Zealand',
    flag: '🇳🇿',
    currency: 'NZD',
    currencySymbol: '$',
    currencyPrefix: 'NZ$',
    locale: 'en-NZ',
    gstRate: 0.15,
    gstLabel: 'GST',
    businessNumberLabel: 'NZBN',
    businessNumberPlaceholder: 'e.g. 9429000000000',
    businessNumberHelp: '13-digit New Zealand Business Number',
    phoneCountryCode: '+64',
    phonePlaceholder: '+64 21 xxx xxxx',
    supportEmail: 'support@handyhub.co.nz',
    domain: 'handyhub.co.nz',
    marketingRegion: 'Auckland and NZ-wide',
    teamLabel: 'Auckland-based team',
    hostSuffixes: ['.co.nz', 'handyhub.co.nz'],
    yearlyPrice: 399,
    yearlySavingsLabel: 'Save NZ$189/year',
  },
  AU: {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺',
    currency: 'AUD',
    currencySymbol: '$',
    currencyPrefix: 'A$',
    locale: 'en-AU',
    gstRate: 0.10,
    gstLabel: 'GST',
    businessNumberLabel: 'ABN',
    businessNumberPlaceholder: 'e.g. 51 824 753 556',
    businessNumberHelp: '11-digit Australian Business Number',
    phoneCountryCode: '+61',
    phonePlaceholder: '+61 4xx xxx xxx',
    supportEmail: 'support@handyhubau.com.au',
    domain: 'handyhubau.com.au',
    marketingRegion: 'across Australia',
    teamLabel: 'Australian-based team',
    hostSuffixes: ['.com.au', 'handyhubau.com.au'],
    yearlyPrice: 499,
    yearlySavingsLabel: 'Save A$89 per year',
  },
};

// ── Cities (full list for onboarding service-area picker) ───────────
export const CITIES_BY_COUNTRY = {
  NZ: ['Auckland', 'Wellington', 'Christchurch', 'Hamilton', 'Tauranga', 'Dunedin', 'Napier', 'Palmerston North', 'Nelson', 'Rotorua'],
  AU: ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Canberra', 'Gold Coast', 'Hobart', 'Darwin'],
};

// ── States/Regions — used when a more granular picker than city is needed ──
export const STATES_BY_COUNTRY = {
  NZ: [
    { code: 'AUK', name: 'Auckland' },
    { code: 'WGN', name: 'Wellington' },
    { code: 'CAN', name: 'Canterbury' },
    { code: 'WKO', name: 'Waikato' },
    { code: 'BOP', name: 'Bay of Plenty' },
    { code: 'OTA', name: 'Otago' },
    { code: 'MWT', name: 'Manawatū-Whanganui' },
    { code: 'HKB', name: "Hawke's Bay" },
    { code: 'NTL', name: 'Northland' },
    { code: 'TKI', name: 'Taranaki' },
    { code: 'STL', name: 'Southland' },
    { code: 'TAS', name: 'Tasman' },
    { code: 'NSN', name: 'Nelson' },
    { code: 'MBH', name: 'Marlborough' },
    { code: 'WTC', name: 'West Coast' },
    { code: 'GIS', name: 'Gisborne' },
  ],
  AU: [
    { code: 'VIC', name: 'Victoria' },
    { code: 'NSW', name: 'New South Wales' },
    { code: 'QLD', name: 'Queensland' },
    { code: 'SA',  name: 'South Australia' },
    { code: 'WA',  name: 'Western Australia' },
    { code: 'TAS', name: 'Tasmania' },
    { code: 'NT',  name: 'Northern Territory' },
    { code: 'ACT', name: 'Australian Capital Territory' },
  ],
};

// ── Suburbs (per major city — used by the onboarding service-area picker) ──
// NZ suburbs already lived in ProviderPages.js; mirrored here so AU has parity.
export const SUBURBS_BY_CITY = {
  // NZ
  Auckland: ['CBD', 'Ponsonby', 'Grey Lynn', 'Mt Eden', 'Newmarket', 'Parnell', 'Remuera', 'Epsom', 'Mt Albert', 'Henderson', 'Manukau', 'Takapuna', 'Albany', 'Mt Wellington', 'Otahuhu'],
  Wellington: ['CBD', 'Thorndon', 'Lambton Quay', 'Newtown', 'Karori', 'Miramar', 'Kilbirnie', 'Petone', 'Lower Hutt'],
  Christchurch: ['CBD', 'Riccarton', 'Merivale', 'Sumner', 'Papanui', 'Fendalton', 'Linwood'],
  Hamilton: ['CBD', 'Hamilton East', 'Frankton', 'Chartwell', 'Rototuna'],
  Tauranga: ['CBD', 'Mount Maunganui', 'Papamoa', 'Bethlehem'],
  Dunedin: ['CBD', 'North Dunedin', 'St Clair', 'Mornington'],
  // AU
  Sydney: ['CBD', 'Parramatta', 'Bondi', 'Manly', 'Chatswood', 'North Sydney', 'Surry Hills', 'Newtown', 'Liverpool', 'Penrith', 'Hornsby', 'Blacktown', 'Hurstville'],
  Melbourne: ['CBD', 'St Kilda', 'Fitzroy', 'Richmond', 'South Yarra', 'Carlton', 'Brunswick', 'Footscray', 'Box Hill', 'Hawthorn', 'Prahran', 'Toorak', 'Docklands'],
  Brisbane: ['CBD', 'Fortitude Valley', 'New Farm', 'West End', 'Toowong', 'South Bank', 'Indooroopilly', 'Chermside'],
  Perth: ['CBD', 'Fremantle', 'Subiaco', 'Cottesloe', 'Joondalup', 'Cannington', 'Scarborough'],
  Adelaide: ['CBD', 'Glenelg', 'North Adelaide', 'Norwood', 'Henley Beach', 'Unley'],
  Canberra: ['Civic', 'Belconnen', 'Tuggeranong', 'Woden', 'Gungahlin', 'Manuka'],
  'Gold Coast': ['Surfers Paradise', 'Broadbeach', 'Burleigh Heads', 'Coolangatta', 'Robina', 'Southport'],
  Hobart: ['CBD', 'Battery Point', 'Sandy Bay', 'North Hobart', 'Glenorchy'],
  Darwin: ['CBD', 'Casuarina', 'Palmerston', 'Stuart Park'],
};

// ── Auto-detection from hostname ────────────────────────────────────
export const resolveCountryFromHost = (host) => {
  if (!host) return null;
  const h = String(host).toLowerCase().split(':')[0];
  // Match longer / more specific suffixes first to handle edge cases
  for (const [code, cfg] of Object.entries(COUNTRY_CONFIG)) {
    for (const suffix of cfg.hostSuffixes) {
      if (h === suffix || h.endsWith(suffix)) return code;
    }
  }
  return null;
};

/**
 * Country resolution order:
 *   1. Explicit override via REACT_APP_DEFAULT_COUNTRY_CODE env var
 *   2. window.location.hostname (production source of truth)
 *   3. localStorage (user toggle in dev/preview)
 *   4. 'NZ' fallback
 */
export const detectCountry = () => {
  const envOverride = (process.env.REACT_APP_DEFAULT_COUNTRY_CODE || '').toUpperCase().trim();
  if (envOverride && COUNTRY_CONFIG[envOverride]) return envOverride;

  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    const fromHost = resolveCountryFromHost(window.location.hostname);
    if (fromHost) return fromHost;
  }

  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = window.localStorage.getItem('handyhub_country');
    if (stored && COUNTRY_CONFIG[stored]) return stored;
  }

  return 'NZ';
};

export const getCountryConfig = (code) => {
  const upper = (code || 'NZ').toUpperCase();
  return COUNTRY_CONFIG[upper] || COUNTRY_CONFIG.NZ;
};

// ── Formatting helpers ──────────────────────────────────────────────
export const formatCurrency = (amount, code) => {
  if (amount == null || isNaN(amount)) return '';
  const cfg = getCountryConfig(code);
  try {
    return new Intl.NumberFormat(cfg.locale, {
      style: 'currency',
      currency: cfg.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${cfg.currencyPrefix}${Number(amount).toFixed(2)}`;
  }
};

// ── Business-number validation (mirrors backend) ────────────────────
/** Australian Business Number — 11 digits, weighted modulo-89 algorithm. */
export const validateABN = (abn) => {
  if (!abn) return false;
  const digits = String(abn).replace(/\D/g, '');
  if (digits.length !== 11) return false;
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const nums = digits.split('').map(d => parseInt(d, 10));
  nums[0] -= 1;
  const total = nums.reduce((sum, n, i) => sum + n * weights[i], 0);
  return total % 89 === 0;
};

/** NZ Business Number — 13 digits (no checksum). */
export const validateNZBN = (nzbn) => {
  if (!nzbn) return false;
  const digits = String(nzbn).replace(/\D/g, '');
  return digits.length === 13;
};

export const validateBusinessNumber = (value, countryCode) => {
  const code = (countryCode || '').toUpperCase();
  if (code === 'AU') return validateABN(value);
  if (code === 'NZ') return validateNZBN(value);
  return Boolean(value && String(value).trim());
};
